import { computed, getCurrentScope, onScopeDispose, ref, toRaw, watch, type Ref } from 'vue';
import { ApiError } from '@/api/client';

/**
 * Список с поиском, фильтрами и страницами — одним куском на все экраны.
 *
 * ЗАЧЕМ. Каждый раздел писал это заново и по-своему: чёрный список тянул
 * двести записей разом и не показывал остальные восемьсот; журнал грузил
 * «ещё» кнопкой, не зная, сколько всего; таблица кандидатов имеет своё,
 * потому что там виртуализация. В итоге на трёх экранах три разных ответа
 * на один вопрос «где я в списке» — и два из них неверные.
 *
 * Здесь собрано ровно то, что у всех одинаково:
 *
 *   • поиск с задержкой — иначе запрос уходит на каждую букву;
 *   • фильтры произвольного вида, лишь бы они умели превращаться в параметры;
 *   • страницы: смещение, размер, общее число, «показано N–M из K»;
 *   • сброс на первую страницу при смене поиска или фильтра. Без этого
 *     человек стоит на пятой странице, меняет фильтр и видит пустоту —
 *     хотя записи есть, просто их меньше пяти страниц.
 *
 * ЧЕГО ЗДЕСЬ НЕТ намеренно: разбора ответа и разметки. Загрузчик приходит
 * снаружи и сам знает, куда ходить и что возвращать, — иначе composable
 * пришлось бы учить всем маршрутам сразу.
 */

export interface ListPage<T> {
  items: T[];
  /** Сколько всего записей под текущим фильтром */
  total: number;
}

export interface ListQueryOptions<T, F extends Record<string, unknown>> {
  /** Куда и как ходить. Получает уже готовые смещение, размер и фильтры. */
  load: (params: { search: string; limit: number; offset: number; filters: F }) => Promise<ListPage<T>>;
  /** Начальные значения фильтров */
  filters?: F;
  /** Сколько строк на странице */
  pageSize?: number;
  /** Задержка перед запросом по мере набора текста */
  searchDelay?: number;
  /** Грузить ли сразу при создании */
  immediate?: boolean;
}

export function useListQuery<T, F extends Record<string, unknown> = Record<string, never>>(
  options: ListQueryOptions<T, F>,
) {
  const pageSize = ref(options.pageSize ?? 50);
  const search = ref('');
  const filters = ref({ ...(options.filters ?? {}) }) as Ref<F>;

  const items = ref<T[]>([]) as Ref<T[]>;
  const total = ref(0);
  const offset = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const page = computed(() => Math.floor(offset.value / pageSize.value) + 1);
  const pages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)));
  const from = computed(() => (total.value === 0 ? 0 : offset.value + 1));
  const to = computed(() => Math.min(offset.value + items.value.length, total.value));
  const hasPrev = computed(() => offset.value > 0);
  const hasNext = computed(() => offset.value + pageSize.value < total.value);

  let requestVersion = 0;
  let disposed = false;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  async function load(): Promise<void> {
    if (disposed) return;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = null;
    const version = ++requestVersion;
    const current = () => !disposed && version === requestVersion;
    loading.value = true;
    error.value = null;
    try {
      const result = await options.load({
        search: search.value.trim(),
        limit: pageSize.value,
        offset: offset.value,
        filters: structuredClone(toRaw(filters.value)),
      });
      if (!current()) return;
      items.value = result.items;
      total.value = result.total;

      /*
        Страница осталась за краем — отступаем на последнюю существующую.

        Так бывает после удаления или смены фильтра: человек стоял на
        седьмой странице, записей стало на три, и он смотрит в пустоту,
        решив, что данные пропали.
      */
      if (offset.value > 0 && result.items.length === 0 && result.total > 0) {
        offset.value = Math.max(0, (Math.ceil(result.total / pageSize.value) - 1) * pageSize.value);
        await load();
      }
    } catch (e) {
      if (!current()) return;
      error.value = e instanceof ApiError ? e.message : 'Не удалось загрузить список';
      items.value = [];
      total.value = 0;
    } finally {
      if (current()) loading.value = false;
    }
  }

  /** Поиск с задержкой: запрос на каждую букву не нужен ни нам, ни серверу. */
  watch(search, () => {
    ++requestVersion;
    loading.value = true;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchTimer = null;
      offset.value = 0;
      void load();
    }, options.searchDelay ?? 300);
  }, { flush: 'sync' });

  // Смена фильтра или размера страницы начинает список сначала
  watch(
    [filters, pageSize],
    () => {
      offset.value = 0;
      void load();
    },
    { deep: true, flush: 'sync' },
  );

  function goTo(next: number): void {
    const bounded = Math.min(Math.max(1, next), pages.value);
    offset.value = (bounded - 1) * pageSize.value;
    void load();
  }

  const next = () => hasNext.value && goTo(page.value + 1);
  const prev = () => hasPrev.value && goTo(page.value - 1);

  /** Перечитать текущую страницу — после правки или удаления записи. */
  const refresh = () => load();

  if (getCurrentScope()) onScopeDispose(() => {
    disposed = true;
    ++requestVersion;
    if (searchTimer) clearTimeout(searchTimer);
  });

  if (options.immediate !== false) void load();

  return {
    items,
    total,
    search,
    filters,
    pageSize,
    offset,
    loading,
    error,
    page,
    pages,
    from,
    to,
    hasPrev,
    hasNext,
    goTo,
    next,
    prev,
    refresh,
  };
}
